import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, 
  Users, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Plus,
  Edit3,
  Eye,
  EyeOff,
  Zap,
  FileText,
  MessageSquare
} from 'lucide-react';
import { 
  MutualActionPlan, 
  MAPMilestone, 
  OnboardingProject 
} from './types';

interface MAPPanelProps {
  projectId?: string;
  userRole: 'client' | 'vendor' | 'admin';
  onConvertToOnboarding?: (mapId: string) => void;
}

export const MAPPanel: React.FC<MAPPanelProps> = ({
  projectId,
  userRole,
  onConvertToOnboarding
}) => {
  const [maps, setMaps] = useState<MutualActionPlan[]>([]);
  const [selectedMAP, setSelectedMAP] = useState<MutualActionPlan | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState<'overview' | 'detail' | 'timeline'>('overview');

  // Mock data - replace with API calls
  useEffect(() => {
    const mockMAPs: MutualActionPlan[] = [
      {
        id: 'map-001',
        name: 'Enterprise CRM Implementation - Sales to CS Handoff',
        description: 'Mutual action plan for TechCorp Solutions CRM implementation',
        salesOwner: 'Alex Johnson',
        csOwner: 'Sarah Chen',
        clientName: 'TechCorp Solutions',
        status: 'accepted',
        clientVisible: true,
        createdAt: '2024-01-15T09:00:00Z',
        updatedAt: '2024-01-20T14:30:00Z',
        milestones: [
          {
            id: 'milestone-001',
            title: 'Requirements Finalization',
            description: 'Complete stakeholder requirements gathering and approval',
            dueDate: '2024-01-25',
            owner: 'Sarah Chen',
            status: 'in-progress',
            clientVisible: true,
            dependencies: [],
            estimatedHours: 16
          },
          {
            id: 'milestone-002',
            title: 'Technical Architecture Review',
            description: 'Review and approve technical architecture design',
            dueDate: '2024-01-30',
            owner: 'Mike Johnson',
            status: 'pending',
            clientVisible: true,
            dependencies: ['milestone-001'],
            estimatedHours: 24
          },
          {
            id: 'milestone-003',
            title: 'Development Environment Setup',
            description: 'Configure development and staging environments',
            dueDate: '2024-02-05',
            owner: 'David Rodriguez',
            status: 'pending',
            clientVisible: false,
            dependencies: ['milestone-002'],
            estimatedHours: 32
          }
        ]
      }
    ];
    setMaps(mockMAPs);
    if (mockMAPs.length > 0) {
      setSelectedMAP(mockMAPs[0]);
    }
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-50';
      case 'in-progress': return 'text-blue-500 bg-blue-50';
      case 'pending': return 'text-yellow-500 bg-yellow-50';
      case 'overdue': return 'text-red-500 bg-red-50';
      case 'draft': return 'text-gray-500 bg-gray-50';
      case 'proposed': return 'text-purple-500 bg-purple-50';
      case 'accepted': return 'text-green-600 bg-green-100';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getMilestoneStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'in-progress': return 'text-blue-500';
      case 'pending': return 'text-yellow-500';
      case 'overdue': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const handleConvertToOnboarding = (mapId: string) => {
    if (onConvertToOnboarding) {
      onConvertToOnboarding(mapId);
    } else {
      console.log('Converting MAP to onboarding plan:', mapId);
      // TODO: Implement conversion logic
    }
  };

  const renderMAPOverview = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900">Mutual Action Plans</h3>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Create MAP
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {maps.map((map) => (
          <motion.div
            key={map.id}
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 cursor-pointer"
            onClick={() => setSelectedMAP(map)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">{map.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{map.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(map.status)}`}>
                {map.status}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>Sales: {map.salesOwner}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>CS: {map.csOwner}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Target className="w-4 h-4" />
                <span>{map.clientName}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Milestones</span>
                <span className="font-medium text-gray-900">
                  {map.milestones.filter(m => m.status === 'completed').length}/{map.milestones.length}
                </span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] h-2 rounded-full transition-all"
                  style={{
                    width: `${(map.milestones.filter(m => m.status === 'completed').length / map.milestones.length) * 100}%`
                  }}
                />
              </div>
            </div>

            {map.status === 'accepted' && !map.convertedToOnboarding && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConvertToOnboarding(map.id);
                }}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg transition-all"
              >
                <Zap className="w-4 h-4" />
                Convert to Onboarding Plan
              </button>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderMAPDetail = () => {
    if (!selectedMAP) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedMAP(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Overview
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900"
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setViewMode(viewMode === 'timeline' ? 'detail' : 'timeline')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900"
            >
              {viewMode === 'timeline' ? <FileText className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
              {viewMode === 'timeline' ? 'Detail' : 'Timeline'}
            </button>
          </div>
        </div>

        <div className="tyler-card p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 drop-shadow-sm">{selectedMAP.name}</h2>
            <p className="text-gray-600 mb-4">{selectedMAP.description}</p>
            
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Sales Owner:</span>
                <span className="font-medium text-gray-900">{selectedMAP.salesOwner}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">CS Owner:</span>
                <span className="font-medium text-gray-900">{selectedMAP.csOwner}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Client:</span>
                <span className="font-medium text-gray-900">{selectedMAP.clientName}</span>
              </div>
            </div>
          </div>

          {viewMode === 'detail' ? (
            <div className="space-y-4">
              {selectedMAP.milestones.map((milestone) => (
                <div key={milestone.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 drop-shadow-sm">{milestone.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {milestone.clientVisible ? (
                        <Eye className="w-4 h-4 text-blue-500" title="Client Visible" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" title="Internal Only" />
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMilestoneStatusColor(milestone.status)}`}>
                        {milestone.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{milestone.owner}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Due: {milestone.dueDate}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{milestone.estimatedHours}h</span>
                    </div>
                  </div>

                  {milestone.dependencies.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Dependencies: </span>
                      <span className="text-xs text-gray-700">{milestone.dependencies.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              {selectedMAP.milestones.map((milestone, index) => (
                <div key={milestone.id} className="relative flex items-start mb-8">
                  <div className="absolute left-2 top-2 w-4 h-4 bg-white border-2 border-gray-300 rounded-full -translate-x-1"></div>
                  <div className="ml-8 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900 drop-shadow-sm">{milestone.title}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMilestoneStatusColor(milestone.status)}`}>
                        {milestone.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{milestone.owner}</span>
                      <span>Due: {milestone.dueDate}</span>
                      <span>{milestone.estimatedHours}h</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Mutual Action Plans
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Sales to CS handoff with clear milestones and client visibility
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedMAP ? 'detail' : 'overview'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {selectedMAP ? renderMAPDetail() : renderMAPOverview()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
